/**
 * Listing Detail Enhancement Components (Tier 5.2.2)
 * 
 * Provides enhanced business listing display features:
 * - Image gallery with lightbox
 * - Business hours with open/closed indicator
 * - Map with directions link
 * - Contact options (phone, email, website)
 * - Token offers section
 */

import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faImage,
  faImages,
  faChevronLeft,
  faChevronRight,
  faTimes,
  faExpand,
  faClock,
  faCheckCircle,
  faTimesCircle,
  faMapMarkerAlt,
  faDirections,
  faPhone,
  faEnvelope,
  faGlobe,
  faExternalLinkAlt,
  faCoins,
  faPercent,
  faCalendarAlt,
  faCopy,
  faCheck
} from '@fortawesome/free-solid-svg-icons';
import { useToast } from './ui';
import TokenSpendIndicator from './TokenSpendIndicator';
import '../styles/ListingDetails.css';

// USVI timezone
const USVI_TIMEZONE = 'America/Virgin';

/**
 * Image Gallery with Lightbox
 */
export function ImageGallery({ images = [], alt = 'Business image' }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [thumbnailStart, setThumbnailStart] = useState(0);

  const THUMBNAILS_VISIBLE = 5;

  // Handle keyboard navigation
  useEffect(() => {
    if (!lightboxOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setLightboxOpen(false);
      if (e.key === 'ArrowLeft') navigatePrev();
      if (e.key === 'ArrowRight') navigateNext();
    };

    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lightboxOpen, currentIndex]);

  const navigateNext = useCallback(() => {
    setCurrentIndex(prev => (prev + 1) % images.length);
  }, [images.length]);

  const navigatePrev = useCallback(() => {
    setCurrentIndex(prev => (prev - 1 + images.length) % images.length);
  }, [images.length]);

  const handleThumbnailClick = (index) => {
    setCurrentIndex(index);
  };

  const scrollThumbnails = (direction) => {
    const maxStart = Math.max(0, images.length - THUMBNAILS_VISIBLE);
    if (direction === 'next') {
      setThumbnailStart(prev => Math.min(prev + 1, maxStart));
    } else {
      setThumbnailStart(prev => Math.max(prev - 1, 0));
    }
  };

  if (!images || images.length === 0) {
    return (
      <div className="gallery-empty">
        <FontAwesomeIcon icon={faImage} />
        <p>No images available</p>
      </div>
    );
  }

  const visibleThumbnails = images.slice(thumbnailStart, thumbnailStart + THUMBNAILS_VISIBLE);

  return (
    <>
      <div className="image-gallery">
        {/* Main Image */}
        <div className="gallery-main" onClick={() => setLightboxOpen(true)}>
          <img 
            src={images[currentIndex]} 
            alt={`${alt} ${currentIndex + 1}`} 
          />
          <button className="expand-btn" title="View full size">
            <FontAwesomeIcon icon={faExpand} />
          </button>
          {images.length > 1 && (
            <div className="image-counter">
              <FontAwesomeIcon icon={faImages} />
              {currentIndex + 1} / {images.length}
            </div>
          )}
        </div>

        {/* Thumbnails */}
        {images.length > 1 && (
          <div className="gallery-thumbnails">
            {thumbnailStart > 0 && (
              <button 
                className="thumbnail-nav prev" 
                onClick={() => scrollThumbnails('prev')}
              >
                <FontAwesomeIcon icon={faChevronLeft} />
              </button>
            )}
            
            <div className="thumbnails-container">
              {visibleThumbnails.map((img, idx) => {
                const actualIndex = thumbnailStart + idx;
                return (
                  <button
                    key={actualIndex}
                    className={`thumbnail ${actualIndex === currentIndex ? 'active' : ''}`}
                    onClick={() => handleThumbnailClick(actualIndex)}
                  >
                    <img src={img} alt={`Thumbnail ${actualIndex + 1}`} />
                  </button>
                );
              })}
            </div>
            
            {thumbnailStart + THUMBNAILS_VISIBLE < images.length && (
              <button 
                className="thumbnail-nav next" 
                onClick={() => scrollThumbnails('next')}
              >
                <FontAwesomeIcon icon={faChevronRight} />
              </button>
            )}
          </div>
        )}
      </div>

      {/* Lightbox — portaled to body so position:fixed anchors to the viewport
          (escapes any ancestor transform/filter/will-change containing block) */}
      {lightboxOpen && createPortal(
        <div className="lightbox-overlay" onClick={() => setLightboxOpen(false)}>
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <button className="lightbox-close" onClick={() => setLightboxOpen(false)} aria-label="Close image viewer">
              <FontAwesomeIcon icon={faTimes} />
            </button>
            
            <button className="lightbox-nav prev" onClick={navigatePrev} aria-label="Previous image">
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>
            
            <div className="lightbox-image">
              <img src={images[currentIndex]} alt={`${alt} ${currentIndex + 1}`} />
            </div>
            
            <button className="lightbox-nav next" onClick={navigateNext} aria-label="Next image">
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
            
            <div className="lightbox-counter">
              {currentIndex + 1} / {images.length}
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}

/**
 * Business Hours Display with Open/Closed Indicator
 */
export function BusinessHours({ hours = {}, compact = false }) {
  const [isOpen, setIsOpen] = useState(null);
  const [currentStatus, setCurrentStatus] = useState('');

  const DAYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  // Check if currently open
  useEffect(() => {
    const checkIfOpen = () => {
      const now = new Date();
      const usviNow = new Date(now.toLocaleString('en-US', { timeZone: USVI_TIMEZONE }));
      const dayOfWeek = DAYS[usviNow.getDay()];
      const currentTime = usviNow.getHours() * 60 + usviNow.getMinutes();
      
      const todayHours = hours[dayOfWeek];
      
      if (!todayHours || todayHours.closed) {
        setIsOpen(false);
        setCurrentStatus('Closed today');
        return;
      }

      const parseTime = (timeStr) => {
        const [hours, minutes] = timeStr.split(':').map(Number);
        return hours * 60 + (minutes || 0);
      };

      const openTime = parseTime(todayHours.open);
      const closeTime = parseTime(todayHours.close);

      if (currentTime >= openTime && currentTime < closeTime) {
        setIsOpen(true);
        const closeIn = closeTime - currentTime;
        if (closeIn <= 60) {
          setCurrentStatus(`Closes in ${closeIn} min`);
        } else {
          setCurrentStatus(`Open until ${todayHours.close}`);
        }
      } else if (currentTime < openTime) {
        setIsOpen(false);
        setCurrentStatus(`Opens at ${todayHours.open}`);
      } else {
        setIsOpen(false);
        setCurrentStatus('Closed');
      }
    };

    checkIfOpen();
    const interval = setInterval(checkIfOpen, 60000); // Update every minute
    
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hours]);

  const hasHours = Object.keys(hours).length > 0;

  if (!hasHours) {
    return (
      <div className="business-hours empty">
        <FontAwesomeIcon icon={faClock} />
        <span>Hours not available</span>
      </div>
    );
  }

  if (compact) {
    return (
      <div className={`business-hours-compact ${isOpen ? 'open' : 'closed'}`}>
        <FontAwesomeIcon icon={isOpen ? faCheckCircle : faTimesCircle} />
        <span className="status">{isOpen ? 'Open' : 'Closed'}</span>
        <span className="detail">{currentStatus}</span>
      </div>
    );
  }

  return (
    <div className="business-hours">
      <div className="hours-header">
        <FontAwesomeIcon icon={faClock} />
        <h4>Business Hours</h4>
        <span className={`open-status ${isOpen ? 'open' : 'closed'}`}>
          <FontAwesomeIcon icon={isOpen ? faCheckCircle : faTimesCircle} />
          {isOpen ? 'Open Now' : 'Closed'}
        </span>
      </div>
      
      {currentStatus && (
        <div className="current-status">{currentStatus}</div>
      )}
      
      <div className="hours-list">
        {DAYS.map((day, idx) => {
          const dayHours = hours[day];
          const today = new Date().getDay() === idx;
          
          return (
            <div key={day} className={`hours-row ${today ? 'today' : ''}`}>
              <span className="day-name">{DAY_LABELS[idx]}</span>
              <span className="day-hours">
                {!dayHours || dayHours.closed 
                  ? 'Closed' 
                  : `${dayHours.open} - ${dayHours.close}`
                }
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Map Display with Directions Link
 */
export function LocationMap({ address, coordinates, businessName }) {
  const { showToast } = useToast();
  const [copied, setCopied] = useState(false);

  // Google Maps URLs
  const mapsSearchUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address || businessName)}`;
  const directionsUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address || businessName)}`;

  const copyAddress = async () => {
    try {
      await navigator.clipboard.writeText(address);
      setCopied(true);
      showToast('Address copied!', 'success');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      showToast('Failed to copy address', 'error');
    }
  };

  if (!address) {
    return (
      <div className="location-map empty">
        <FontAwesomeIcon icon={faMapMarkerAlt} />
        <p>Location not available</p>
      </div>
    );
  }

  return (
    <div className="location-map">
      <div className="map-header">
        <FontAwesomeIcon icon={faMapMarkerAlt} />
        <h4>Location</h4>
      </div>
      
      {/* Static Map Preview - would need Google Static Maps API */}
      <div className="map-preview">
        <div className="map-placeholder">
          <FontAwesomeIcon icon={faMapMarkerAlt} />
          <p>Map Preview</p>
          <a href={mapsSearchUrl} target="_blank" rel="noopener noreferrer">
            View on Google Maps
          </a>
        </div>
      </div>
      
      <div className="address-display">
        <p className="address-text">{address}</p>
        <button 
          className={`copy-btn ${copied ? 'copied' : ''}`}
          onClick={copyAddress}
          title="Copy address"
        >
          <FontAwesomeIcon icon={copied ? faCheck : faCopy} />
        </button>
      </div>
      
      <div className="map-actions">
        <a 
          href={directionsUrl} 
          target="_blank" 
          rel="noopener noreferrer"
          className="directions-btn"
        >
          <FontAwesomeIcon icon={faDirections} />
          Get Directions
        </a>
        <a 
          href={mapsSearchUrl} 
          target="_blank" 
          rel="noopener noreferrer"
          className="view-map-btn"
        >
          <FontAwesomeIcon icon={faExternalLinkAlt} />
          Open in Maps
        </a>
      </div>
    </div>
  );
}

/**
 * Contact Options
 */
export function ContactOptions({ phone, email, website, onContact }) {
  const { showToast } = useToast();
  const [copiedField, setCopiedField] = useState(null);

  const copyToClipboard = async (text, fieldName) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(fieldName);
      showToast(`${fieldName} copied!`, 'success');
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      showToast('Failed to copy', 'error');
    }
  };

  const handleContact = (type, value) => {
    if (onContact) {
      onContact(type, value);
    }
  };

  const hasContact = phone || email || website;

  if (!hasContact) {
    return (
      <div className="contact-options empty">
        <p>No contact information available</p>
      </div>
    );
  }

  return (
    <div className="contact-options">
      <h4>Contact</h4>
      
      {phone && (
        <div className="contact-row">
          <a 
            href={`tel:${phone.replace(/\D/g, '')}`}
            className="contact-link phone"
            onClick={() => handleContact('phone', phone)}
          >
            <FontAwesomeIcon icon={faPhone} />
            <span>{phone}</span>
          </a>
          <button 
            className={`copy-btn ${copiedField === 'Phone' ? 'copied' : ''}`}
            onClick={() => copyToClipboard(phone, 'Phone')}
          >
            <FontAwesomeIcon icon={copiedField === 'Phone' ? faCheck : faCopy} />
          </button>
        </div>
      )}
      
      {email && (
        <div className="contact-row">
          <a 
            href={`mailto:${email}`}
            className="contact-link email"
            onClick={() => handleContact('email', email)}
          >
            <FontAwesomeIcon icon={faEnvelope} />
            <span>{email}</span>
          </a>
          <button 
            className={`copy-btn ${copiedField === 'Email' ? 'copied' : ''}`}
            onClick={() => copyToClipboard(email, 'Email')}
          >
            <FontAwesomeIcon icon={copiedField === 'Email' ? faCheck : faCopy} />
          </button>
        </div>
      )}
      
      {website && (
        <div className="contact-row">
          <a 
            href={website.startsWith('http') ? website : `https://${website}`}
            target="_blank"
            rel="noopener noreferrer"
            className="contact-link website"
            onClick={() => handleContact('website', website)}
          >
            <FontAwesomeIcon icon={faGlobe} />
            <span>{website.replace(/^https?:\/\//, '')}</span>
            <FontAwesomeIcon icon={faExternalLinkAlt} className="external-icon" />
          </a>
        </div>
      )}
    </div>
  );
}

/**
 * Token Offers Section
 */
export function TokenOffersSection({ offers = [], onRedeem, userBalance = 0, userId = null }) {
  if (!offers || offers.length === 0) {
    return (
      <div className="token-offers-section empty">
        <FontAwesomeIcon icon={faCoins} />
        <p>No token offers available at this time</p>
      </div>
    );
  }

  return (
    <div className="token-offers-section">
      <div className="offers-header">
        <div className="offers-header__title">
          <FontAwesomeIcon icon={faCoins} />
          <h4>Token Offers</h4>
          <span className="offer-count">{offers.length} available</span>
        </div>
        {userId && (
          <TokenSpendIndicator userId={userId} src="token-offers" />
        )}
      </div>
      
      <div className="offers-list">
        {offers.map(offer => (
          <TokenOfferItem
            key={offer.id}
            offer={offer}
            onRedeem={onRedeem}
            userBalance={userBalance}
          />
        ))}
      </div>
    </div>
  );
}

/**
 * Individual Token Offer Item
 */
function TokenOfferItem({ offer, onRedeem, userBalance }) {
  const canAfford = userBalance >= offer.tokenCost;
  const isExpired = offer.expiresAt && new Date(offer.expiresAt) < new Date();
  const isLimitedQty = offer.maxRedemptions && offer.redemptionCount >= offer.maxRedemptions;
  
  const getOfferIcon = () => {
    if (offer.type === 'discount') return faPercent;
    return faCoins;
  };

  const formatExpiry = (date) => {
    if (!date) return null;
    const d = new Date(date);
    return d.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      timeZone: USVI_TIMEZONE 
    });
  };

  return (
    <div className={`token-offer-item ${!canAfford ? 'insufficient' : ''} ${isExpired || isLimitedQty ? 'unavailable' : ''}`}>
      <div className="offer-icon">
        <FontAwesomeIcon icon={getOfferIcon()} />
      </div>
      
      <div className="offer-info">
        <h5 className="offer-title">{offer.title}</h5>
        {offer.description && (
          <p className="offer-description">{offer.description}</p>
        )}
        
        <div className="offer-meta">
          {offer.expiresAt && (
            <span className="offer-expiry">
              <FontAwesomeIcon icon={faCalendarAlt} />
              Expires {formatExpiry(offer.expiresAt)}
            </span>
          )}
          {offer.maxRedemptions && (
            <span className="offer-limit">
              {offer.maxRedemptions - (offer.redemptionCount || 0)} left
            </span>
          )}
        </div>
      </div>
      
      <div className="offer-action">
        <div className="offer-cost">
          <FontAwesomeIcon icon={faCoins} />
          <span>{offer.tokenCost}</span>
        </div>
        
        <button
          className="redeem-btn"
          onClick={() => onRedeem && onRedeem(offer)}
          disabled={!canAfford || isExpired || isLimitedQty}
        >
          {isExpired ? 'Expired' : isLimitedQty ? 'Sold Out' : !canAfford ? 'Need More' : 'Redeem'}
        </button>
      </div>
    </div>
  );
}

/**
 * Full Listing Detail View Component
 * Combines all the above components into a complete detail view
 */
export function ListingDetailView({ 
  listing, 
  onClose, 
  onRedeem, 
  onContact,
  userBalance = 0,
  userId = null 
}) {
  if (!listing) return null;

  return (
    <div className="listing-detail-view">
      {/* Gallery */}
      <ImageGallery 
        images={listing.images || (listing.image ? [listing.image] : [])} 
        alt={listing.name} 
      />
      
      {/* Header */}
      <div className="detail-header">
        <h2>{listing.name}</h2>
        <span className="listing-type">{listing.type}</span>
        <BusinessHours hours={listing.hours} compact />
      </div>
      
      {/* Description */}
      {listing.description && (
        <div className="detail-description">
          <p>{listing.description}</p>
        </div>
      )}
      
      {/* Contact & Location Grid */}
      <div className="detail-grid">
        <ContactOptions
          phone={listing.phone}
          email={listing.email || listing.contact}
          website={listing.website}
          onContact={onContact}
        />
        
        <LocationMap
          address={listing.location || listing.address}
          coordinates={listing.coordinates}
          businessName={listing.name}
        />
      </div>
      
      {/* Business Hours (Full) */}
      {listing.hours && Object.keys(listing.hours).length > 0 && (
        <BusinessHours hours={listing.hours} />
      )}
      
      {/* Token Offers */}
      <TokenOffersSection
        offers={listing.tokenOffers || listing.discounts || []}
        onRedeem={onRedeem}
        userBalance={userBalance}
        userId={userId}
      />
    </div>
  );
}

export default ListingDetailView;
